import { React, useState, useEffect, useCallback } from "react";
import { api } from "../../utils/api";
import { useParams } from "react-router-dom";
import { Button, FormControl, InputLabel, MenuItem, Select, Alert } from "@mui/material";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import ModalPopup from "../../components/ModalPopup";
import VehicleForm from "./Forms/VehicleForm";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import BasicUserInfo, { DATA_GRID_STYLES } from "../../components/BasicUserInfo";
import { COLORS } from "../../utils/colors";
import { subscriptionRequiresContract } from "../../utils/subscriptionContractGate";
import { isPendingCancellation } from "../../utils/subscriptionDisplayStatus";

const VehicleSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [editVehicleState, setEditVehicleState] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleOwner, setVehicleOwner] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [targetVehicleId, setTargetVehicleId] = useState("");
  const [deleteError, setDeleteError] = useState(null);

  const fetchVehicleDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api("v1/vehicle/" + params.idVehicle, "GET", null);
      if (response.data.success) {
        const activeSubscriptions = await api(`v1/subscription/search`, "POST", {
          query: { vehicle: params.idVehicle },
        });
        if (activeSubscriptions.data.success) {
          response.data.data.vehicle.activeSubscriptions = activeSubscriptions.data.data.searchResult;
        } else {
          response.data.data.vehicle.activeSubscriptions = [];
        }

        const updatedProductInfo = await Promise.all(
          response.data.data.vehicle.products.map(async (product) => {
            const fetchProductInfo = await api("v1/product/" + product._id, "GET");
            let productData = null;
            if (fetchProductInfo.data.success) {
              productData = fetchProductInfo.data.data.product;
            }
            return productData;
          })
        );
        response.data.data.vehicle.products = updatedProductInfo;

        setSelectedVehicle(response.data.data.vehicle);
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  }, [params.idVehicle]);

  useEffect(() => {
    fetchVehicleDetails();
  }, [fetchVehicleDetails]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchVehicleDetails();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchVehicleDetails]);

  useEffect(() => {
    const fetchVehicleOwnerDetails = async () => {
      if (selectedVehicle != null) {
        try {
          let response = await api("v1/customer/" + selectedVehicle.customer, "GET", null);
          if (response.data.success) {
            console.log(response.data.data);
            response.data.data.customer.vehicles = null;
            setVehicleOwner(response.data.data.customer);
            setLoading(false);
          }
        } catch (error) {
          console.error("API error:", error);
        }
      }
    };
    fetchVehicleOwnerDetails();
  }, [selectedVehicle]);

  const loadDeletePreview = async () => {
    setDeletePreviewLoading(true);
    setDeleteError(null);
    try {
      const response = await api(`v1/vehicle/${params.idVehicle}/delete-preview`, "GET", null);
      if (response?.data?.success) {
        setDeletePreview(response.data.data.preview);
      } else {
        setDeleteError(response?.data?.message || "Could not load delete preview.");
      }
    } catch (error) {
      console.error("API error:", error);
      setDeleteError("Could not load delete preview.");
    }
    setDeletePreviewLoading(false);
  };

  const duplicateMergeTargets =
    deletePreview?.duplicateSiblingVehicles?.length > 0
      ? deletePreview.duplicateSiblingVehicles
      : (deletePreview?.siblingVehicles || []).filter((v) => v.isDuplicateRego);

  const handleDeleteVehicleConfirm = async () => {
    setLoading(true);
    setDeleteError(null);
    try {
      const payload = {};
      if (targetVehicleId) payload.targetVehicleId = targetVehicleId;

      const response = await api("v1/vehicle/" + selectedVehicle._id, "DELETE", payload);
      if (response?.data?.success) {
        showSuccessToast(response.data.message || "Vehicle deleted successfully.");
        if (selectedVehicle.customer) {
          navigate(`/customer/${selectedVehicle.customer}`);
        } else {
          navigate("/vehicle");
        }
        return;
      }

      const message = response?.data?.message || "Failed to delete vehicle.";
      setDeleteError(message);
      if (response?.data?.data?.blockers) {
        setDeletePreview((prev) => ({
          ...prev,
          blockers: response.data.data.blockers,
          requiresTargetVehicle: true,
        }));
      }
      showErrorToast(message);
    } catch (error) {
      console.error("API error:", error);
      showErrorToast("Failed to delete vehicle.");
    }
    setLoading(false);
  };

  const toggleDisplayDeleteConfirm = async () => {
    const opening = !deleteConfirmationIsOpen;
    setDeleteConfirmationIsOpen(opening);
    if (opening) {
      setTargetVehicleId("");
      setDeletePreview(null);
      setDeleteError(null);
      await loadDeletePreview();
    }
  };

  const editVehicle = () => {
    setEditVehicleState((prevValue) => !prevValue);
    // setOpenModal(true);
  };

  const reloadVehicleDetail = async (updatedVehicleDetails) => {
    setEditVehicleState((prevValue) => !prevValue);

    const updatedProductInfo = await Promise.all(
      updatedVehicleDetails.products.map(async (product) => {
        const fetchProductInfo = await api("v1/product/" + product._id, "GET");
        let productData = null;
        if (fetchProductInfo.data.success) {
          productData = fetchProductInfo.data.data.product;
        }
        return productData;
      })
    );

    updatedVehicleDetails.products = updatedProductInfo;
    setSelectedVehicle(updatedVehicleDetails);
  };

  console.log("editVehicleState", editVehicleState);

  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);

    return formattedDate;
  }

  console.log("SELECTED VEHICEL", selectedVehicle);
  return (
    selectedVehicle && (
      <div className="page partner-user-single w-full flex flex-column items-center min-h-h-screen pt-4 gap-8 p-8">
        {loading && <GlobalLoadingSpinner />}

        {/* Page Title */}
        <div className="pageTitle w-full flex justify-between">
          <div>
            <h1>Vehicle Details</h1>
          </div>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>

        {/* Vehicle Single Content */}
        <div className="w-full flex flex-column md:flex-row  gap-8">
          <div className="w-full md:w-[500px]">
            <h2 className="font-semibold">Vehicle</h2>
            <div className={DATA_GRID_STYLES}>
              <span>REGO</span>
              <span>{selectedVehicle.rego}</span>
              <span>VIN</span>
              <span>{selectedVehicle.vin}</span>
              <span>MAKE</span>
              <span>{selectedVehicle.make}</span>
              <span>MODEL</span>
              <span>{selectedVehicle.model}</span>
              <span>MODEL YEAR</span>
              <span>{selectedVehicle.modelYear}</span>
              <span>AGE</span>
              <span>{selectedVehicle.age}</span>
              <span>Purchase Order Number</span>
              <span>{selectedVehicle.poNumber}</span>
              <span>Stock Code</span>
              <span>{selectedVehicle.stockCode}</span>
              <span>VEHICLE TYPE</span>
              <span>
                {selectedVehicle.vehicleType === "sedan"
                  ? "Sedan"
                  : selectedVehicle.vehicleType === "wagon"
                  ? "Wagon / SUV"
                  : selectedVehicle.vehicleType === "4wd"
                  ? "4WD"
                  : "(not set)"}
              </span>
              <span>PRODUCTS</span>
              <div className="flex align-center ">
                {selectedVehicle.products.map((field, index) =>
                  field ? (
                    <span className="m-auto p-1 bg-white border-solid border-1 border-[#E0E0E0] rounded-lg" key={index}>
                      <a
                        href="javascript:void(0);"
                        onClick={() =>
                          sessionStorage.getItem("selectedPortal") != "Customer" && navigate("/product/" + field._id)
                        }
                      >
                        <strong>{field.name} </strong>
                      </a>
                    </span>
                  ) : (
                    ""
                  )
                )}
              </div>
              <span>ACTIVE</span>
              <span>{selectedVehicle.active ? "Yes" : "No"}</span>
              <span>CREATED ON</span>
              <span>{formatDate(selectedVehicle.createdOnUTC)}</span>
            </div>
            <div>
              {(sessionStorage.getItem("selectedPortal") == "Administrator" ||
                sessionStorage.getItem("selectedPortal") == "Customer") && (
                <div className="actionItems flex gap-4">
                  <Button
                    size="large"
                    sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                    className="self-start m-4"
                    variant="contained"
                    onClick={editVehicle}
                  >
                    Edit Vehicle
                  </Button>
                  {sessionStorage.getItem("selectedPortal") == "Administrator" && (
                    <Button
                      size="large"
                      className="self-start bg-red-600 m-4"
                      variant="contained"
                      color="error"
                      onClick={() => {
                        toggleDisplayDeleteConfirm(true);
                      }}
                    >
                      Delete Vehicle
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full md:w-[500px]">
            <h2 className="font-semibold">Customer</h2>
            {vehicleOwner && <BasicUserInfo user={vehicleOwner} />}
          </div>
        </div>

        {(sessionStorage.getItem("selectedPortal") == "Administrator" ||
          sessionStorage.getItem("selectedPortal") == "Customer") &&
          selectedVehicle?.activeSubscriptions?.length > 0 && (
            <div className="subscriptions-of-vehicle w-full md:w-[500px] flex flex-column self-start border p-2 shadow">
              <h3>Active Subscriptions</h3>
              <div className="flex flex-wrap gap-5">
                {selectedVehicle.activeSubscriptions.map((subscription) => {
                  const activeClass = subscription.active ? "" : "inactive";
                  const isAddOn = subscription.note && subscription.note.mainSubscriptionId;
                  const borderColor = isAddOn ? "border-purple-400 border-l-4" : "border-blue-400 border-l-4";

                  return (
                    <div
                      key={subscription._id}
                      className={`flex flex-column justify-center items-center cursor-pointer border p-2 hover:bg-sky-100 hover:ring-sky-500 ${borderColor}`}
                      onClick={() => {
                        navigate(`/subscription/${subscription._id}`);
                      }}
                    >
                      <div className="w-full flex justify-between items-start mb-1">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isAddOn ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {isAddOn ? "Add-on" : "Main Subscription"}
                        </span>
                        <span
                          className={`${
                            isPendingCancellation(subscription)
                              ? "bg-yellow-500"
                              : subscription.active
                              ? "bg-teal-500"
                              : "bg-rose-500"
                          } text-white border rounded px-1 flex justify-center text-xs`}
                        >
                          {isPendingCancellation(subscription)
                            ? "Pending Cancellation"
                            : subscription.active
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </div>
                      {(() => {
                        const now = new Date();
                        const isExpired =
                          subscription.validUntilDateUTC && new Date(subscription.validUntilDateUTC) < now;
                        const isFuture =
                          subscription.validFromDateUTC && new Date(subscription.validFromDateUTC) > now;
                        const isValid = !isExpired && !isFuture;
                        const requiresContract = subscriptionRequiresContract(
                          subscription.product,
                          subscription
                        );
                        const hasQRCode =
                          subscription?.qrcode && String(subscription.qrcode).trim() !== "";

                        if (isValid && hasQRCode) {
                          return (
                            <img src={subscription.qrcode} alt="Subscription QR code" className="max-w-[200px]" />
                          );
                        }
                        if (isValid && requiresContract && !hasQRCode) {
                          return (
                            <div className="p-4 bg-gray-100 text-center text-xs max-w-[200px]">
                              QR code available after signing the membership contract
                            </div>
                          );
                        }
                        return (
                          <div className="p-4 bg-gray-100 text-center text-xs max-w-[200px]">
                            {isExpired ? "Expired" : isFuture ? "Not yet valid" : "No QR code"}
                          </div>
                        );
                      })()}
                      {subscription.validUntilDateUTC && (
                        <div className="text-xs text-gray-600 mt-2 text-center">
                          <span className="font-semibold">Expires:</span> {formatDate(subscription.validUntilDateUTC)}
                        </div>
                      )}
                      {(() => {
                        const requiresContract = subscriptionRequiresContract(
                          subscription.product,
                          subscription
                        );
                        const hasQRCode =
                          subscription?.qrcode && String(subscription.qrcode).trim() !== "";
                        if (!requiresContract || hasQRCode) {
                          return <p className="text-xs mt-1">{subscription._id}</p>;
                        }
                        return (
                          <p className="text-xs mt-1 text-gray-500 text-center">
                            Membership ID hidden until contract is signed
                          </p>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        {/* // Edit Modal HTML */}

        <ModalPopup
          showModal={editVehicleState}
          handleShowModal={editVehicle}
          popupTitle={"Edit Vehicle"}
          popupContent={
            <VehicleForm selectedVehicle={selectedVehicle} callback={reloadVehicleDetail} closeModal={editVehicle} />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        {/* Delete Vehicle Confimation Modal */}
        <ModalPopup
          showModal={deleteConfirmationIsOpen}
          handleShowModal={toggleDisplayDeleteConfirm}
          popupTitle={"Delete Vehicle"}
          popupContent={
            <div className="flex flex-col gap-3 text-left">
              <p>
                Delete vehicle{" "}
                <strong>
                  {selectedVehicle.rego} ({selectedVehicle.vin})
                </strong>
                ?
              </p>

              {deletePreviewLoading && <p className="text-sm text-gray-600">Checking linked records…</p>}

              {deleteError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {deleteError}
                </Alert>
              )}

              {deletePreview?.blockers?.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                  <p className="font-semibold mb-2">Linked records on this vehicle:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {deletePreview.blockers.map((b, i) => (
                      <li key={i}>{b.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {deletePreview?.canMergeToDuplicate && duplicateMergeTargets.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel id="merge-target-vehicle-label">Merge into duplicate vehicle (optional)</InputLabel>
                  <Select
                    labelId="merge-target-vehicle-label"
                    label="Merge into duplicate vehicle (optional)"
                    value={targetVehicleId}
                    onChange={(e) => setTargetVehicleId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Do not merge — keep records on this vehicle</em>
                    </MenuItem>
                    {duplicateMergeTargets.map((v) => (
                      <MenuItem key={v._id} value={v._id}>
                        {v.rego || "No rego"} — {v.vin || "N/A"} ({v.active === false ? "inactive" : "active"})
                      </MenuItem>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-600 mt-2">
                    Records are only moved when rego matches (duplicate vehicle). Otherwise they stay on this
                    unlinked vehicle after deletion.
                  </p>
                </FormControl>
              )}

              {deletePreview?.recordsRemainOnVehicle && !targetVehicleId && (
                <Alert severity="info">
                  {deletePreview?.canMergeToDuplicate
                    ? "Delete without selecting a merge target: linked records remain on this vehicle (removed from the customer list only)."
                    : "Rego does not match another vehicle on this customer. Linked records will remain on this unlinked vehicle."}
                </Alert>
              )}
            </div>
          }
          cancelButtonText={"Cancel"}
          confirmButtonText={"Delete"}
          confirmCallbackAction={handleDeleteVehicleConfirm}
        />
      </div>
    )
  );
};

export default VehicleSingle;
